import {
  EmptyPageDex,
  ErrorPageDex,
} from '../../../research/components/shared/Common';
import Table from 'modules/shared-components/data-display/table';
import Header from 'modules/shared-components/data-display/table/header';
import {
  GetDexOpenPositionsResponse,
  OpenPositionListItem,
} from 'api/types/httpsTypes/dex-trade';

import { OpenTradesFiltersBar } from './OpenTradesFilters';
import { desktopHistoryColumns, desktopOpenTradesColumns } from './columns';
import { TableRow } from './TableRow';
import { twMerge } from 'tailwind-merge';
import { useOpenTradesSearchFilters } from './usePageParams';
import { forwardRef, useMemo, useRef, useState } from 'react';
import Button from 'modules/shared-components/button/button';
import { ClosePosition } from '../../../trade-monitor/ClosePosition';
import { useToggle } from 'modules/utils/useToggle';
import { useChainInfo } from 'api/hooks/useChainInfo';
import { TokenColumn } from './columnsComponents/tokenColumn';
import { useGlobalChain } from 'modules/routing/chainProvider';
import { Pagination } from 'modules/shared-components/data-display/table/pagination';
import { useHistoryTradesSettings } from 'modules/routing/mainSettingsProvider';
import Checkbox from '../../../components/inputs/Checkbox';
import { FullScreenModal } from '../../../components/modal/FullScreenModal';
import { PositionDetails } from '../../../trade-monitor/components/Details/PositionDetails';

export function thDynamicClass(s: string) {
  return s === '' ? 'col-span-1' : '';
}

interface Props {
  isOpen: boolean;
  isActive?: boolean;
  page: GetDexOpenPositionsResponse | undefined;
  error: boolean | undefined;
  isLoading: boolean;
  isTerminal?: boolean;
}

export const TradesComponent = forwardRef<HTMLTableRowElement, Props>(
  ({ isActive, isOpen, page, error, isLoading, isTerminal }, ref) => {
    const [selectedTokens, setSelectedTokens] = useState<string[]>([]); // Explicit type
    const [openCloseAllModal, { open, close }] = useToggle();
    const [selectedChain] = useGlobalChain();
    const chainAssetPrice = useChainInfo(selectedChain)?.price;
    const { isFailedTradesHistoryHidden, setIsHistoryTradesFailedHidden } =
      useHistoryTradesSettings();
    const [openPositionId, setOpenPositionId] = useState<string | null>(null);

    const trades = page?.data ?? [];
    const totalLoaded = page?.data?.length ?? 0;
    const totalAvailable = page?.pagination?.total ?? 0;
    const hasMore = totalLoaded < totalAvailable;

    const columns = useMemo(() => {
      if (!isOpen) return desktopHistoryColumns;

      const baseColumns = desktopOpenTradesColumns({
        chainAssetPrice,
        isTerminal,
      });

      const tokenColumn = TokenColumn({
        trades,
        selectedTokens,
        setSelectedTokens,
      });

      return [tokenColumn, ...baseColumns];
    }, [
      isOpen,
      chainAssetPrice,
      selectedTokens,
      setSelectedTokens,
      trades,
      isTerminal,
    ]);

    const listRef = useRef<HTMLTableElement>(null);
    const { goToPage } = useOpenTradesSearchFilters();

    function scrollAndNavigate(pageNumber: number) {
      goToPage(pageNumber);
      if (listRef.current) {
        listRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }

    return (
      <div className="space-y-2">
        <div className="flex space-x-1 items-center">
          <div
            className={
              isTerminal
                ? 'xxs:p-2 flex justify-between items-center w-full'
                : 'xxs:pl-2'
            }
          >
            <div className="flex xxs:space-x-1 items-center">
              {!isTerminal && <OpenTradesFiltersBar isTerminal={isTerminal} />}
              {!isTerminal && (
                <div className="flex items-center xxs:gap-1">
                  <Checkbox
                    id="isFailedTradesHistoryHidden"
                    className="lg:w-4 lg:h-4 xxs:w-5 xxs:h-5"
                    checked={isFailedTradesHistoryHidden}
                    onChange={() =>
                      setIsHistoryTradesFailedHidden(
                        !isFailedTradesHistoryHidden
                      )
                    }
                  />
                  <Checkbox.Label htmlFor="isFailedTradesHistoryHidden">
                    <div className="xxs:text-sm lg:text-xs xxs:text-dex-white-secondary hover:text-dex-white">
                      Hide Failed
                    </div>
                  </Checkbox.Label>
                </div>
              )}
              {selectedTokens && selectedTokens.length >= 1 && (
                <div>
                  <Button
                    type="button"
                    onClick={open}
                    className="xxs:m-0 xxs:px-2 xxs:py-1"
                    variant="errorDex"
                  >
                    <span className="normal-case">
                      Bulk Sell ({selectedTokens.length})
                    </span>
                  </Button>
                  {openCloseAllModal &&
                    page?.data &&
                    page?.data?.length >= 1 && (
                      <ClosePosition
                        setSelectedTokens={setSelectedTokens}
                        position={page.data.filter(
                          (trade: OpenPositionListItem) =>
                            selectedTokens &&
                            selectedTokens.length >= 1 &&
                            selectedTokens.includes(trade.id)
                        )}
                        onCancel={close}
                      />
                    )}
                </div>
              )}
            </div>
          </div>
        </div>

        {error ? (
          <ErrorPageDex />
        ) : (
          <div className="overflow-x-auto">
            <div
              className={twMerge(
                !isTerminal && 'lg:min-w-[1300px] ',
                'w-full xxs:space-y-1'
              )}
            >
              <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
                <Header
                  trClassName={twMerge(
                    isTerminal
                      ? 'grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                      : twMerge(
                          isOpen
                            ? 'grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_100px]'
                            : 'grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                        ),
                    'xxs:dark:bg-dex-black-900 xxs:grid'
                  )}
                  columns={columns}
                  thDynamicClass={thDynamicClass}
                  rowClassName="xxs:px-2 xxs:py-1"
                  className="xxs:text-xs lg:grid xxs:dark:bg-dex-black-900"
                />
                <tbody className="lg:block lg:h-terminal overflow-y-auto">
                  {isLoading ? (
                    [...Array(10).keys()]
                      .map((i) => i + 1)
                      .map((index) => (
                        <Table.LoadingRow
                          thDynamicClass={thDynamicClass}
                          key={index}
                          elementClassName="xxs:dark:bg-dex-black-700"
                          className={twMerge(
                            isTerminal
                              ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                              : twMerge(
                                  isOpen
                                    ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_100px]'
                                    : 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                                ),
                            'lg:grid xxs:dark:bg-dex-black-900'
                          )}
                          columns={columns}
                        />
                      ))
                  ) : page?.data?.length === 0 ? (
                    <EmptyPageDex height="100px" title="No Trades Found" />
                  ) : (
                    <>
                      {page?.data?.map((item, rowId) => (
                        <TableRow
                          isTerminal={isTerminal}
                          isOpen={isOpen}
                          onOpen={() => setOpenPositionId(item.id)}
                          key={
                            item.tokenAddress + '_' + item.chain + '_' + rowId
                          }
                          data={item}
                          columnsDef={columns}
                        />
                      ))}
                      {isActive && hasMore && (
                        <>
                          <Table.LoadingRow
                            cellClassName="xxs:py-3"
                            elementClassName="xxs:dark:bg-dex-black-700"
                            className={twMerge(
                              isTerminal
                                ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                                : twMerge(
                                    isOpen
                                      ? 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_100px]'
                                      : 'lg:grid-cols-[250px_1fr_1fr_1fr_1fr_1fr_100px]'
                                  ),
                              'lg:grid xxs:dark:bg-dex-black-900'
                            )}
                            columns={columns}
                          />

                          <tr>
                            <td colSpan={columns.length}>
                              <div ref={ref} style={{ height: 1 }} />
                            </td>
                          </tr>
                        </>
                      )}
                    </>
                  )}
                </tbody>
              </Table>
              {openPositionId && (
                <FullScreenModal
                  hideHeader
                  className="xxs:p-0 lg:p-0 xxs:pb-16"
                  close={() => setOpenPositionId(null)}
                >
                  <PositionDetails id={openPositionId} />
                </FullScreenModal>
              )}
              {!isActive && page && page?.data?.length !== 0 && (
                <div className="xxs:p-2">
                  <Pagination
                    title="Trades"
                    goToPage={scrollAndNavigate}
                    activePage={page.pagination}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);
